import { useState, useCallback, useEffect, useRef } from 'react';
import DashboardService from '../services/DashboardService';
import { showConfirmMessage, showSuccessMessage, showErrorMessage } from '../utils/dashboardMessageUtils';
import { findBestPositionForNewChart } from '../utils/dashboardLayoutUtils';

/**
 * @typedef {Object} HistoryEntry
 * @property {string} actionType - 액션 타입
 * @property {string} description - 액션 설명
 * @property {Object} template - 저장된 템플릿 상태
 * @property {Array} layout - 저장된 레이아웃 상태
 * @property {number} timestamp - 타임스탬프
 * @property {string} componentId - 컴포넌트 ID
 */

/**
 * @typedef {Object} DashboardState
 * @property {Object} template - 대시보드 템플릿
 * @property {Array} layout - 레이아웃 정보
 */

/**
 * 대시보드 편집 모드를 관리하는 커스텀 훅
 * @param {Object} initialLayouts - 초기 레이아웃 설정
 * @param {Object} dashboardTemplate - 대시보드 템플릿
 * @param {Function} setDashboardTemplateAndUpdateData - 템플릿 업데이트 함수
 * @param {Array} selectedComponentIds - 선택된 컴포넌트 ID 배열
 * @param {Function} triggerComponentResize - 컴포넌트 리사이즈 트리거 함수
 * @returns {Object} 편집 모드 관련 상태와 함수들
 */
export const useDashboardEditMode = (initialLayouts = { lg: [] }, dashboardTemplate, setDashboardTemplateAndUpdateData, selectedComponentIds = [], triggerComponentResize = null) => {
  const [editMode, setEditMode] = useState(false);
  const [layouts, _internalSetLayouts] = useState(initialLayouts);
  const [hasLayoutChanged, setHasLayoutChanged] = useState(false);
  const [originalLayout, setOriginalLayout] = useState([]);
  const [originalTemplate, setOriginalTemplate] = useState(null);
  const [isSavingLayout, setIsSavingLayout] = useState(false);
  // compactType 초기값 - 템플릿 로드 전에는 compaction을 비활성화(null)
  const [compactType, setCompactType] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  
  // 현재 활성 브레이크포인트 추적
  const [currentBreakpoint, setCurrentBreakpoint] = useState('lg');
  
  // 드래그 관련 상태
  const [isDragging, setIsDragging] = useState(false);
  const [draggedComponent, setDraggedComponent] = useState(null);
  
  // Undo/Redo 히스토리 스택 상태
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [maxHistorySize] = useState(20);
  const [isUndoRedoOperation, setIsUndoRedoOperation] = useState(false);
  const [isChatbotAddingComponent, setIsChatbotAddingComponent] = useState(false);
  
  // 디바운싱을 위한 ref
  const debounceTimerRef = useRef(null);
  const lastLayoutRef = useRef(null);
  const isUserInteractionRef = useRef(false);
  
  // 즉시 동기화를 위한 RAF ref
  const rafIdRef = useRef(null);
  const lastRafTimeRef = useRef(0);
  
  // 최신 dashboardTemplate 참조를 위한 ref
  const dashboardTemplateRef = useRef(dashboardTemplate);
  
  // 드래그 관련 ref
  const dragStartTimeRef = useRef(new Map()); // 컴포넌트별 드래그 시작 시간

  // dashboardTemplate 변경 시 ref 업데이트
  useEffect(() => {
    dashboardTemplateRef.current = dashboardTemplate;
  }, [dashboardTemplate]);

  // dashboardTemplate 변경 시 저장된 layout_mode로 compactType 동기화
  useEffect(() => {
    if (!dashboardTemplate) {
      return;
    }

    // 템플릿에서 layout_mode 읽어오고 정규화
    const savedLayoutMode = dashboardTemplate?.dashboard_options?.layout_mode;
    const normalizedSaved = (savedLayoutMode && ['vertical', 'horizontal', 'none'].includes(savedLayoutMode))
      ? savedLayoutMode
      : null;

    // 동일 대시보드 ID 편집 중이라도 템플릿 붙여넣기 등으로 layout_mode가 바뀌면 동기화
    if (normalizedSaved !== compactType) {
      setCompactType(normalizedSaved);
    }
  }, [dashboardTemplate, compactType]);

  // setLayouts 별칭 함수
  const setLayouts = useCallback((newLayouts) => {
    _internalSetLayouts(newLayouts);
  }, []);

  /**
   * 통합된 히스토리 액션을 생성합니다.
   * @param {string} actionType - 액션 타입
   * @param {string} componentId - 컴포넌트 ID
   * @param {string} description - 액션 설명
   * @returns {HistoryEntry} 히스토리 엔트리
   */
  const createHistoryAction = useCallback((actionType, componentId, description) => {
    const currentDashboardTemplate = dashboardTemplateRef.current;
    
    return {
      actionType,
      description,
      template: currentDashboardTemplate ? JSON.parse(JSON.stringify(currentDashboardTemplate)) : null,
      layout: layouts[currentBreakpoint] || layouts.lg ? JSON.parse(JSON.stringify(layouts[currentBreakpoint] || layouts.lg)) : [],
      timestamp: Date.now(),
      componentId
    };
  }, [layouts, currentBreakpoint]);

  // addToHistory 별칭 함수 (savePreActionState 기반)
  const addToHistory = useCallback((actionType, description, componentId = null) => {
    const currentDashboardTemplate = dashboardTemplateRef.current;
    
    if (isUndoRedoOperation || !currentDashboardTemplate || !editMode) {
      return;
    }

    const historyEntry = createHistoryAction(actionType, componentId, description);
    
    setUndoStack(prev => {
      const newStack = [...prev, historyEntry];
      return newStack.slice(-maxHistorySize);
    });
    
    setRedoStack([]);
  }, [editMode, maxHistorySize, isUndoRedoOperation, createHistoryAction]);

  /**
   * 현재 대시보드 상태를 반환합니다.
   * @returns {DashboardState} 현재 대시보드 상태
   */
  const getCurrentDashboardState = useCallback(() => {
    const currentDashboardTemplate = dashboardTemplateRef.current;
    return {
      template: currentDashboardTemplate ? JSON.parse(JSON.stringify(currentDashboardTemplate)) : null,
      layout: layouts[currentBreakpoint] || layouts.lg ? JSON.parse(JSON.stringify(layouts[currentBreakpoint] || layouts.lg)) : []
    };
  }, [layouts, currentBreakpoint]);

  /**
   * 액션 시작 전 상태를 저장합니다.
   * @param {string} actionType - 액션 타입
   * @param {string} componentId - 컴포넌트 ID
   * @param {string} description - 액션 설명
   * @param {boolean} forceMode - 강제 모드 (편집 모드 조건 무시)
   */
  const savePreActionState = useCallback((actionType, componentId, description = '', forceMode = false) => {
    const currentDashboardTemplate = dashboardTemplateRef.current;
    
    if (isUndoRedoOperation || !currentDashboardTemplate || (!editMode && !forceMode)) {
      return;
    }

    const historyEntry = createHistoryAction(actionType, componentId, description);

    setUndoStack(prev => {
      const newStack = [...prev, historyEntry];
      return newStack.slice(-maxHistorySize);
    });
    
    setRedoStack([]);
  }, [editMode, maxHistorySize, isUndoRedoOperation, createHistoryAction]);

  /**
   * 액션과 함께 히스토리를 저장하는 통합 함수입니다.
   * @param {string} actionType - 액션 타입
   * @param {string} componentId - 컴포넌트 ID
   * @param {string} description - 액션 설명
   * @param {Function} callback - 실행할 콜백 함수
   */
  const executeWithHistory = useCallback((actionType, componentId, description, callback) => {
    savePreActionState(actionType, componentId, description);
    callback();
  }, [savePreActionState]);

  /**
   * 히스토리 스택을 초기화합니다.
   */
  const clearHistory = useCallback(() => {
    setUndoStack([]);
    setRedoStack([]);
  }, []);

  /**
   * 효과적인 압축 타입을 반환합니다.
   * @returns {string|null} 압축 타입
   */
  const getEffectiveCompactType = useCallback(() => {
    // 특정 작업 중에는 압축 비활성화
    if (isUndoRedoOperation || isChatbotAddingComponent) {
      return null;
    }
    
    // 드래그 중이 아니면 정상적으로 compactType 반환
    return compactType;
  }, [isUndoRedoOperation, isChatbotAddingComponent, compactType, isDragging]);

  /**
   * 레이아웃과 템플릿을 동기화합니다.
   * @param {Object} newLayoutsObj - 새 레이아웃 객체
   * @param {string} source - 호출 소스
   * @param {boolean} forceUpdate - 강제 업데이트 여부
   * @param {boolean} isCancelOperation - 취소 작업 여부
   */
  const syncLayoutAndTemplate = useCallback((newLayoutsObj, source = 'unknown') => {
    // z-order 변경 중이면 템플릿 업데이트 건너뛰기
    if (window._isZOrderChanging) {
      return false;
    }

    // 현재 브레이크포인트의 레이아웃 또는 lg 레이아웃 사용
    const layoutToUse = newLayoutsObj?.[currentBreakpoint] || newLayoutsObj?.lg;
    
    if (!dashboardTemplate?.components || !layoutToUse) {
      return false;
    }

    const currentComponents = dashboardTemplate.components;
    let hasAnyChange = false;
    let templateNeedsUpdate = false;

    const updatedComponents = currentComponents.map(component => {
      const componentId = component.id;
      const layoutItem = layoutToUse.find(item => item.i === componentId);
      
      if (!layoutItem) {
        return component;
      }

      const currentPosition = component.position || {};
      const newPosition = {
        x: layoutItem.x,
        y: layoutItem.y,
        w: layoutItem.w,
        h: layoutItem.h
      };

      // 레이아웃이 실제로 변경되었는지 확인
      const hasLayoutChanged = 
        currentPosition.x !== newPosition.x ||
        currentPosition.y !== newPosition.y ||
        currentPosition.w !== newPosition.w ||
        currentPosition.h !== newPosition.h;

      if (hasLayoutChanged) {
        hasAnyChange = true;
        templateNeedsUpdate = true;
        return {
          ...component,
          position: newPosition
        };
      }

      return component;
    });

    if (templateNeedsUpdate) {
      const updatedTemplate = {
        ...dashboardTemplate,
        components: updatedComponents
      };
      
      // 🎯 그룹의 original_layout 업데이트 (개별 수정 반영)
      if (updatedTemplate.groups && updatedTemplate.groups.length > 0) {
        updatedTemplate.groups = updatedTemplate.groups.map(group => {
          // 이 그룹에 속한 컴포넌트들의 현재 레이아웃 가져오기
          const groupComponentIds = updatedComponents
            .filter(c => c.group_id === group.id)
            .map(c => c.id);
          
          if (groupComponentIds.length > 0) {
            // 현재 레이아웃으로 original_layout 업데이트
            const newOriginalLayout = layoutToUse
              .filter(item => groupComponentIds.includes(item.i))
              .map(item => ({
                id: item.i,
                x: item.x,
                y: item.y,
                w: item.w,
                h: item.h
              }));
            
            return {
              ...group,
              original_layout: newOriginalLayout
            };
          }
          
          return group;
        });
      }
      
      setDashboardTemplateAndUpdateData(updatedTemplate);
      setHasLayoutChanged(true);
    }

    return hasAnyChange;
  }, [dashboardTemplate, setDashboardTemplateAndUpdateData, setHasLayoutChanged, currentBreakpoint]);

  /**
   * 커스텀 레이아웃 설정 함수입니다.
   * @param {Object|Function} newLayoutsInput - 새 레이아웃 또는 업데이트 함수
   */
  const customSetLayouts = useCallback((newLayoutsInput) => {
    _internalSetLayouts(prevLayouts => {
      const newLayouts = typeof newLayoutsInput === 'function' ? newLayoutsInput(prevLayouts) : newLayoutsInput;
      syncLayoutAndTemplate(newLayouts, "customSetLayouts");
      return newLayouts;
    });
  }, [syncLayoutAndTemplate]);

  /**
   * 레이아웃에서 컴포넌트를 제거합니다.
   * @param {string} componentId - 제거할 컴포넌트 ID
   */
  const removeComponentFromLayout = useCallback((componentId, skipHistorySaving = false) => {
    const removeFn = () => {
      customSetLayouts(prev => {
        const updatedLayouts = {};
        ['lg', 'md', 'sm', 'xs', 'xxs'].forEach(bp => {
          updatedLayouts[bp] = (prev[bp] || []).filter(item => item.i !== componentId);
        });
        return updatedLayouts;
      });
    };
    if (skipHistorySaving) {
      removeFn();
    } else {
      executeWithHistory('remove_component', componentId, `차트 삭제: ${componentId}`, removeFn);
    }
  }, [customSetLayouts, executeWithHistory]);

  const canUndo = undoStack.length > 0;
  const canRedo = redoStack.length > 0;

  /**
   * 실행 취소 (Undo)를 수행합니다.
   */
  const undo = useCallback(() => {
    if (!canUndo || !editMode) return;

    setIsUndoRedoOperation(true);

    const lastHistoryEntry = undoStack[undoStack.length - 1];
    const { template: historyTemplate, layout: historyLayout } = lastHistoryEntry;
    
    const currentDashboardTemplate = dashboardTemplateRef.current;
    const redoEntry = {
      actionType: lastHistoryEntry.actionType,
      description: lastHistoryEntry.description,
      template: JSON.parse(JSON.stringify(currentDashboardTemplate)),
      layout: layouts[currentBreakpoint] || layouts.lg ? JSON.parse(JSON.stringify(layouts[currentBreakpoint] || layouts.lg)) : [],
      timestamp: Date.now(),
    };
    
    setRedoStack(prev => [...prev, redoEntry]);
    setUndoStack(prev => prev.slice(0, -1));
    
    // 모든 브레이크포인트에 동일한 레이아웃 적용
    _internalSetLayouts({
      lg: historyLayout,
      md: historyLayout,
      sm: historyLayout,
      xs: historyLayout,
      xxs: historyLayout
    });
    setDashboardTemplateAndUpdateData(historyTemplate);
    setHasLayoutChanged(true);
    
    setTimeout(() => {
      setIsUndoRedoOperation(false);
    }, 50);
    
  }, [canUndo, editMode, undoStack, layouts, currentBreakpoint, setDashboardTemplateAndUpdateData]);

  /**
   * 다시 실행 (Redo)를 수행합니다.
   */
  const redo = useCallback(() => {
    if (!canRedo || !editMode) return;

    setIsUndoRedoOperation(true);

    const nextHistoryEntry = redoStack[redoStack.length - 1];
    const { template: historyTemplate, layout: historyLayout } = nextHistoryEntry;
    
    const currentDashboardTemplate = dashboardTemplateRef.current;
    const undoEntry = {
      actionType: nextHistoryEntry.actionType,
      description: nextHistoryEntry.description,
      template: JSON.parse(JSON.stringify(currentDashboardTemplate)),
      layout: layouts[currentBreakpoint] || layouts.lg ? JSON.parse(JSON.stringify(layouts[currentBreakpoint] || layouts.lg)) : [],
      timestamp: Date.now(),
    };
    
    setUndoStack(prev => [...prev, undoEntry]);
    setRedoStack(prev => prev.slice(0, -1));
    
    // 모든 브레이크포인트에 동일한 레이아웃 적용
    _internalSetLayouts({
      lg: historyLayout,
      md: historyLayout,
      sm: historyLayout,
      xs: historyLayout,
      xxs: historyLayout
    });
    setDashboardTemplateAndUpdateData(historyTemplate);
    setHasLayoutChanged(true);
    
    setTimeout(() => {
      setIsUndoRedoOperation(false);
    }, 50);
    
  }, [canRedo, editMode, redoStack, layouts, currentBreakpoint, setDashboardTemplateAndUpdateData]);

  /**
   * 선택된 컴포넌트들을 키보드로 이동시킵니다.
   * @param {array} componentIds - 이동할 컴포넌트 ID들
   * @param {string} direction - 이동 방향 ('up', 'down', 'left', 'right')
   */
  const moveComponentsByKeyboard = useCallback((componentIds, direction) => {
    if (!editMode || !componentIds || componentIds.length === 0) return;
    
    const currentLayout = layouts[currentBreakpoint] || layouts.lg || [];
    
    // 선택된 컴포넌트들의 레이아웃 항목들 찾기
    const selectedLayoutItems = currentLayout.filter(item => componentIds.includes(item.i));
    
    if (selectedLayoutItems.length === 0) return;
    
    // 현재 템플릿의 컬럼 수 가져오기
    const columns = dashboardTemplate?.layout?.columns || 12;
    
    // 이동 가능한지 체크 (경계 검사)
    let canMove = true;
    let deltaX = 0;
    let deltaY = 0;
    
    switch (direction) {
      case 'left':
        deltaX = -1;
        canMove = selectedLayoutItems.every(item => item.x > 0);
        break;
      case 'right':
        deltaX = 1;
        canMove = selectedLayoutItems.every(item => item.x + item.w < columns);
        break;
      case 'up':
        deltaY = -1;
        canMove = selectedLayoutItems.every(item => item.y > 0);
        break;
      case 'down':
        deltaY = 1;
        // 아래로는 항상 이동 가능
        break;
    }
    
    if (!canMove) return;
    
    // 히스토리에 저장
    const desc = componentIds.length === 1 
      ? `키보드로 차트 이동: ${componentIds[0]}`
      : `키보드로 차트 ${componentIds.length}개 이동`;
    savePreActionState('keyboard_move_multiple', componentIds[0], desc);
    
    // 모든 선택된 컴포넌트들의 위치 업데이트
    const updatedLayout = currentLayout.map(item => {
      if (componentIds.includes(item.i)) {
        return {
          ...item,
          x: item.x + deltaX,
          y: item.y + deltaY
        };
      }
      return item;
    });
    
    // 모든 브레이크포인트에 적용
    _internalSetLayouts({
      lg: updatedLayout,
      md: updatedLayout,
      sm: updatedLayout,
      xs: updatedLayout,
      xxs: updatedLayout
    });
    
    // 템플릿과 동기화
    syncLayoutAndTemplate({ [currentBreakpoint]: updatedLayout, lg: updatedLayout }, 'keyboard_move_multiple');
    setHasLayoutChanged(true);
  }, [editMode, layouts, currentBreakpoint, dashboardTemplate, savePreActionState, syncLayoutAndTemplate]);
  
  // 하위 호환성을 위한 wrapper 함수
  const moveComponentByKeyboard = useCallback((componentId, direction) => {
    moveComponentsByKeyboard([componentId], direction);
  }, [moveComponentsByKeyboard]);

  /**
   * 키보드 단축키를 처리합니다.
   * @param {KeyboardEvent} event - 키보드 이벤트
   */
  const handleKeyDown = useCallback((event) => {
    if (!editMode) return;
    // 입력/에디터 포커스 시 전역 Undo/Redo 비활성화
    try {
      const activeEl = document.activeElement;
      const tag = (activeEl && activeEl.tagName) ? activeEl.tagName.toLowerCase() : '';
      const isEditableTag = tag === 'input' || tag === 'textarea' || tag === 'select';
      const isContentEditable = !!(activeEl && activeEl.isContentEditable);
      const hasTextboxRole = !!(activeEl && activeEl.getAttribute && activeEl.getAttribute('role') === 'textbox');
      const insideMonaco = !!(activeEl && activeEl.closest && activeEl.closest('.monaco-editor'));
      const insideTemplateEditor = !!(activeEl && activeEl.closest && (activeEl.closest('.dashboard-options-editor-content') || activeEl.closest('.template-editor-container')));
      if (isEditableTag || isContentEditable || hasTextboxRole || insideMonaco || insideTemplateEditor) {
        return; // 텍스트/에디터 컨텍스트에서는 전역 Undo/Redo 비활성화
      }
    } catch (_) {}

    // 화살표 키로 선택된 컴포넌트들 이동
    if (selectedComponentIds && selectedComponentIds.length > 0 && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      event.preventDefault();
      const direction = event.key.replace('Arrow', '').toLowerCase();
      moveComponentsByKeyboard(selectedComponentIds, direction);
      return;
    }

    // Ctrl+Z (Undo)
    if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
      event.preventDefault();
      undo();
      return;
    }

    // Ctrl+Y 또는 Ctrl+Shift+Z (Redo)
    if ((event.ctrlKey || event.metaKey) && (event.key === 'y' || (event.key === 'z' && event.shiftKey))) {
      event.preventDefault();
      redo();
      return;
    }
  }, [editMode, undo, redo, moveComponentsByKeyboard, selectedComponentIds]);

  // 키보드 이벤트 리스너 등록
  useEffect(() => {
    if (editMode) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [editMode, handleKeyDown]);

  // 공통 함수: 레이아웃 고정
  const fixLayout = useCallback((layout) => {
    lastLayoutRef.current = JSON.parse(JSON.stringify(layout));
  }, []);

  // 공통 함수: 편집 모드 상태 체크
  const canProceedWithEdit = useCallback(() => {
    return !isCancelling && editMode && !isUndoRedoOperation;
  }, [isCancelling, editMode, isUndoRedoOperation]);

  // 공통 함수: 디바운싱 동기화 (리사이즈 완료 후 사용)
  const debouncedSync = useCallback((layout, source, delay = 30) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      fixLayout(layout);
    }, delay);
  }, [fixLayout]);

  // 공통 함수: 즉시 동기화 (실시간 드래그/리사이즈) - 성능 최적화
  const immediateSync = useCallback((layout, source) => {
    const now = performance.now();
    
    // 80ms (≈12.5fps) 제한으로 성능/체감 균형 최적화
    if (now - lastRafTimeRef.current < 80) {
      // 이미 예약된 RAF가 있으면 취소
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      
      // 새로운 RAF 예약 - Layout-only 업데이트
      rafIdRef.current = requestAnimationFrame(() => {
        lastRafTimeRef.current = performance.now();
        fixLayout(layout);
        // 🚀 성능 최적화: 드래그 중에는 레이아웃만 업데이트, 템플릿 동기화 제외
        _internalSetLayouts({ lg: layout });
        rafIdRef.current = null;
      });
    } else {
      // 즉시 실행 - Layout-only 업데이트
      lastRafTimeRef.current = now;
      fixLayout(layout);
      // 🚀 성능 최적화: 드래그 중에는 레이아웃만 업데이트, 템플릿 동기화 제외
      _internalSetLayouts({ lg: layout });
    }
  }, [fixLayout]);

  // 공통 함수: 최종 레이아웃 적용 (드래그/리사이즈 완료 후)
  const applyFinalLayout = useCallback((layout, source) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    
    fixLayout(layout);
    
    setTimeout(() => {
      // 현재 브레이크포인트와 lg 모두에 적용
      syncLayoutAndTemplate({ 
        [currentBreakpoint]: layout,
        lg: layout 
      }, source, true, false);
    }, 100);
  }, [fixLayout, syncLayoutAndTemplate, currentBreakpoint]);

  /**
   * 브레이크포인트 변경을 처리합니다.
   * @param {string} newBreakpoint - 새로운 브레이크포인트
   * @param {number} newCols - 새로운 컬럼 수
   */
  const handleBreakpointChange = useCallback((newBreakpoint, newCols) => {
    setCurrentBreakpoint(newBreakpoint);
  }, []);

  /**
   * 레이아웃 변경을 처리합니다.
   * @param {Array} currentLayout - 현재 레이아웃
   * @param {Object} allLayouts - 모든 레이아웃
   */
  const handleLayoutChange = useCallback((currentLayout, allLayouts) => {
    // z-order 변경 중이면 레이아웃 변경 무시
    if (window._isZOrderChanging) {
      return;
    }

    // 더 자세한 디버깅 정보
    if (currentLayout && currentLayout.length > 0) {
    }
    if (allLayouts) {
    }
    
    if (isCancelling || isUndoRedoOperation) {
      return;
    }
    
    // 저장 직후 편집 모드 변경으로 인한 불필요한 레이아웃 변경 방지
    if (!editMode && !isUserInteractionRef.current) {
      return;
    }
    
    isUserInteractionRef.current = true;
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      const hasActualChange = !lastLayoutRef.current || 
        JSON.stringify(lastLayoutRef.current) !== JSON.stringify(currentLayout);
      
      if (hasActualChange) {
        fixLayout(currentLayout);
        // 모든 브레이크포인트에 currentLayout 저장
        _internalSetLayouts(prevLayouts => ({
          ...prevLayouts,
          [currentBreakpoint]: currentLayout,
          lg: currentLayout  // lg도 항상 업데이트 (fallback용)
        }));
        // 템플릿도 동기화
        syncLayoutAndTemplate({ [currentBreakpoint]: currentLayout, lg: currentLayout }, 'handleLayoutChange');
        setHasLayoutChanged(true);
      }
    }, 50);
  }, [isCancelling, isUndoRedoOperation, editMode, fixLayout, syncLayoutAndTemplate, setHasLayoutChanged, currentBreakpoint]);

  // 드래그 이벤트 핸들러들
  const handleDragStart = useCallback((layout, oldItem, newItem, placeholder, e, element) => {
    if (!editMode) return;

    // double-click-handled 클래스가 있으면 드래그 차단
    if (element && element.classList.contains('double-click-handled')) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    setIsDragging(true);
    setDraggedComponent(newItem.i);
    
    // 이동 시작 전 상태를 Undo 히스토리에 저장 (전체 스냅샷)
    if (dashboardTemplate?.components) {
      savePreActionState('drag', newItem.i, `차트 이동: ${newItem.i}`);
    }
  }, [editMode, dashboardTemplate?.components, savePreActionState]);

  const handleDrag = useCallback((layout, oldItem, newItem, placeholder, e, element) => {
    const componentId = oldItem.i;
    const dragStartTime = dragStartTimeRef.current.get(componentId);
    
    // 드래그 시작 시간이 없으면 (더블클릭으로 취소된 경우) 무시
    if (!dragStartTime) {
      return;
    }
    
    if (!canProceedWithEdit()) return;
    
    isUserInteractionRef.current = true;
    immediateSync(layout, "handleDrag");
  }, [canProceedWithEdit, immediateSync]);

  const handleDragStop = useCallback((layout, oldItem, newItem, placeholder, e, element) => {
    if (!editMode) return;

    setIsDragging(false);
    setDraggedComponent(null);

    // 현재 브레이크포인트와 lg 모두에 layout 저장
    const newLayoutsObj = { 
      [currentBreakpoint]: layout,
      lg: layout  // fallback용
    };
    setLayouts(prevLayouts => ({
      ...prevLayouts,
      ...newLayoutsObj
    }));
    
    // 드래그가 실제로 위치를 변경했는지 확인
    const positionChanged = oldItem.x !== newItem.x || oldItem.y !== newItem.y;
    if (positionChanged) {
      setHasLayoutChanged(true);
      
      // 상하밀기가 설정된 경우 강제로 compaction 트리거
      if (compactType === 'vertical') {
        // 잠시 후 레이아웃을 다시 설정하여 compaction 강제 실행
        setTimeout(() => {
          _internalSetLayouts(prev => ({ ...prev })); // 강제 리렌더링으로 compaction 트리거
        }, 100);
      }
    }
    
    // 레이아웃 동기화
    syncLayoutAndTemplate(newLayoutsObj, 'handleDragStop');
  }, [editMode, syncLayoutAndTemplate, setHasLayoutChanged, compactType, currentBreakpoint]);

  // 리사이즈 이벤트 핸들러들
  const handleResizeStart = useCallback((layout, oldItem, newItem, placeholder, e, element) => {
    if (canProceedWithEdit()) {
      isUserInteractionRef.current = true;
      savePreActionState('resize', oldItem.i, `차트 크기 조정: ${oldItem.i}`);
    }
  }, [canProceedWithEdit, savePreActionState]);

  const handleResize = useCallback((layout, oldItem, newItem, placeholder, e, element) => {
    if (!canProceedWithEdit()) return;
    
    isUserInteractionRef.current = true;
    immediateSync(layout, "handleResize");
  }, [canProceedWithEdit, immediateSync]);

  const handleResizeStop = useCallback((layout, oldItem, newItem, placeholder, e, element) => {
    if (!canProceedWithEdit()) return;
    
    // 리사이즈가 실제로 크기를 변경했는지 확인
    const sizeChanged = oldItem.w !== newItem.w || oldItem.h !== newItem.h;
    if (sizeChanged) {
      setHasLayoutChanged(true);
      
      // ECharts 등 차트 리사이즈 트리거 (릴리즈 후 리렌더링)
      if (triggerComponentResize && newItem.i) {
        triggerComponentResize(newItem.i);
      }
    }
    
    applyFinalLayout(layout, "handleResizeStop");
  }, [canProceedWithEdit, applyFinalLayout, setHasLayoutChanged, triggerComponentResize]);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  /**
   * 레이아웃을 저장합니다.
   */
  const saveLayout = useCallback(async () => {
    const currentDashboardTemplate = dashboardTemplateRef.current;
    if (!currentDashboardTemplate) {
      showErrorMessage('Cannot save layout: Dashboard template is not loaded.');
      return false;
    }
    if (!hasLayoutChanged) {
        // showSuccessMessage('No layout changes to save.'); // 모달 제거
        return true; // 변경사항이 없어도 성공으로 처리
    }

    setIsSavingLayout(true);
    try {
      currentDashboardTemplate.components?.forEach(comp => {
      });
      // 현재 브레이크포인트의 레이아웃 사용 (없으면 lg 사용)
      const currentLayoutData = layouts[currentBreakpoint] || layouts.lg || [];
      // 디버깅: 실제 위치 값 확인
      if (currentLayoutData.length > 0) {
      }

      // 현재 레이아웃을 템플릿의 components에 반영
      // 중요: 템플릿의 기존 position을 무시하고 layout에서 직접 가져온 값 사용
      const updatedComponents = (currentDashboardTemplate.components || []).map(comp => {
        const layoutItem = currentLayoutData.find(item => item.i === comp.id);
        if (layoutItem) {
          // layoutItem에서 직접 가져온 값이 최신 값
          const newX = layoutItem.x;
          const newY = layoutItem.y;
          const newW = layoutItem.w;
          const newH = layoutItem.h;
          
          // 새로운 컴포넌트 객체 생성 (기존 position 완전히 대체)
          const updatedComp = {
            ...comp,
            position: {
              x: newX,
              y: newY,
              w: newW,
              h: newH
            }
          };
          
          // 최종 값 확인
          return updatedComp;
        }
        return comp;
      });

      // 현재 compactType이 유효한 경우에만 dashboard_options.layout_mode에 저장
      const allowedLayoutModes = ['vertical', 'horizontal', 'none'];
      const baseDashboardOptions = {
        ...(currentDashboardTemplate.dashboard_options || {})
      };
      const updatedDashboardOptions = allowedLayoutModes.includes(compactType)
        ? { ...baseDashboardOptions, layout_mode: compactType }
        : baseDashboardOptions;

      // dashboard_id와 dashboardId 필드 일관성 보장
      const dashboardId = currentDashboardTemplate.dashboard_id || currentDashboardTemplate.dashboardId;
      const requestObj = {
        ...currentDashboardTemplate,
        components: updatedComponents,  // 업데이트된 컴포넌트 위치 정보
        dashboard_id: dashboardId,  // 백엔드에서 사용하는 필드명
        dashboardId: dashboardId,    // 프론트엔드에서 사용하는 필드명
        dashboard_options: updatedDashboardOptions
      };
      
      // 최종 확인: 전송될 컴포넌트 위치
      requestObj.components.forEach(comp => {
      });

      // 현재 편집 중인 대시보드 ID를 추출하여 백엔드에 전달
      const currentDashboardId = dashboardId;
      const response = await DashboardService.saveDashboard(requestObj, currentDashboardId);

      if (response.success) {
        // showSuccessMessage('대시보드 레이아웃이 저장되었습니다.'); // 모달 제거
        setHasLayoutChanged(false);
        clearHistory();
        
        // 로컬 템플릿 상태를 저장한 내용으로 즉시 동기화하여 compactType(layout_mode) 되돌림 방지
        setDashboardTemplateAndUpdateData(requestObj);
        
        // 서버 응답을 무시하고 현재 편집한 상태를 유지
        // 현재 레이아웃을 원본으로 설정 (편집 모드 종료 시 복원용)
        setOriginalLayout([...layouts.lg]);
        setOriginalTemplate(JSON.parse(JSON.stringify(requestObj)));
        
        // 서버 응답을 로그로만 확인
        if (response.data && response.data.dashboard_template) {
          // 서버 응답의 컴포넌트 위치 확인
          if (response.data.dashboard_template.template && response.data.dashboard_template.template.components) {
            response.data.dashboard_template.template.components.forEach(c => {
            });
          }
        }
        
        return true; // 저장 성공
      } else {
        showErrorMessage(`Failed to save layout: ${response.error || 'Unknown error'}`);
        return false; // 저장 실패
      }
    } catch (err) {
      console.error("[useDashboardEditMode] Error saving layout:", err);
      showErrorMessage('An error occurred while saving the layout.');
      return false; // 저장 실패
    } finally {
      setIsSavingLayout(false);
    }
  }, [hasLayoutChanged, setDashboardTemplateAndUpdateData, layouts, currentBreakpoint, clearHistory, compactType]);

  /**
   * 변경사항을 취소합니다.
   */
  const cancelChanges = useCallback(async () => {
    setIsCancelling(true);
    setHasLayoutChanged(false);
    clearHistory();
    
    if (originalTemplate) {
      setDashboardTemplateAndUpdateData(originalTemplate);
      
      if (originalLayout && originalLayout.length > 0) {
        const originalLayoutsObj = { lg: [...originalLayout] };
        _internalSetLayouts(originalLayoutsObj);
      }
      
    } else {
      if (originalLayout && originalLayout.length > 0) {
        const originalLayoutsObj = { lg: [...originalLayout] };
        _internalSetLayouts(originalLayoutsObj);
        
        const currentDashboardTemplate = dashboardTemplateRef.current;
        if (currentDashboardTemplate && currentDashboardTemplate.components) {
          const restoredComponents = currentDashboardTemplate.components.map(comp => {
            if (!comp || !comp.id) return comp;
            const originalLayoutItem = originalLayout.find(item => item.i === comp.id);
            if (originalLayoutItem) {
              return {
                ...comp,
                position: {
                  x: originalLayoutItem.x,
                  y: originalLayoutItem.y,
                  w: originalLayoutItem.w,
                  h: originalLayoutItem.h
                }
              };
            }
            return comp;
          });
          
          const restoredTemplate = {
            ...currentDashboardTemplate,
            components: restoredComponents
          };
          
          setDashboardTemplateAndUpdateData(restoredTemplate);
        }
      }
    }
    
    setTimeout(() => {
      setIsCancelling(false);
    }, 200);
  }, [originalTemplate, originalLayout, setDashboardTemplateAndUpdateData, clearHistory]);

  /**
   * 저장 후 편집 모드를 종료합니다.
   */
  const saveAndExit = useCallback(async () => {
    const saveResult = await saveLayout();
    // 저장이 성공한 경우에만 편집 모드 종료
    if (saveResult !== false) {  // saveLayout이 실패시 false를 반환하도록 수정 필요
      // 편집 모드 종료 전에 사용자 상호작용 플래그 리셋
      isUserInteractionRef.current = false;
      
      setEditMode(false);
      document.body.classList.remove('edit-mode-active');
      setOriginalTemplate(null);
      setOriginalLayout([]);
      // 대시보드 목록 새로고침 (is_hidden_in_list 변경 반영)
      // editMode가 false로 전파되기 전에 호출될 수 있으므로 더 긴 지연 시간 필요
      if (window.refreshDashboardList && typeof window.refreshDashboardList === 'function') {
        setTimeout(() => {
          window.refreshDashboardList();
        }, 500); // editMode 상태가 전파되도록 충분한 지연
      }
    } else {
    }
  }, [saveLayout]);

  /**
   * 저장하지 않고 편집 모드를 종료합니다.
   */
  const exitWithoutSaving = useCallback(() => {
    // 항상 확인 메시지를 표시하도록 변경
    showConfirmMessage(
      '변경사항이 저장되지 않습니다. 정말 나가시겠습니까?',
      () => {
        // 변경사항 유무와 관계없이 항상 cancelChanges 호출
        if (originalTemplate) {
          cancelChanges();
        }
        setEditMode(false);
        document.body.classList.remove('edit-mode-active');
        setOriginalTemplate(null);
        setOriginalLayout([]);
      }
    );
  }, [cancelChanges, originalTemplate, originalLayout, editMode]);

  /**
   * 편집 모드를 토글합니다.
   */
  const toggleEditMode = useCallback(() => {
    if (isSavingLayout) return;

    const nextEditMode = !editMode;
    setEditMode(nextEditMode);
    if (nextEditMode) {
      const currentDashboardTemplate = dashboardTemplateRef.current;
      
      // 편집 모드 진입 시 저장된 layout_mode 로드
      if (currentDashboardTemplate && currentDashboardTemplate.dashboard_options) {
        const savedLayoutMode = currentDashboardTemplate.dashboard_options.layout_mode;
        if (savedLayoutMode && ['vertical', 'horizontal', 'none'].includes(savedLayoutMode)) {
          setCompactType(savedLayoutMode);
        }
      }
      
      setOriginalLayout(layouts.lg ? JSON.parse(JSON.stringify(layouts.lg)) : []);
      setOriginalTemplate(currentDashboardTemplate ? JSON.parse(JSON.stringify(currentDashboardTemplate)) : null);
      document.body.classList.add('edit-mode-active');
      clearHistory();
    } else {
      document.body.classList.remove('edit-mode-active');
      clearHistory();
    }
  }, [editMode, layouts, isSavingLayout, clearHistory]);

  /**
   * 현재 템플릿과 레이아웃을 명시적으로 백업합니다.
   */
  const backupCurrentTemplate = useCallback(() => {
    const currentDashboardTemplate = dashboardTemplateRef.current;
    setOriginalLayout(layouts.lg ? JSON.parse(JSON.stringify(layouts.lg)) : []);
    setOriginalTemplate(currentDashboardTemplate ? JSON.parse(JSON.stringify(currentDashboardTemplate)) : null);
  }, [layouts]);

  /**
   * 특정 템플릿과 레이아웃을 백업합니다.
   * @param {Object} templateToBackup - 백업할 템플릿
   * @param {Object} layoutsToBackup - 백업할 레이아웃
   */
  const backupSpecificTemplate = useCallback((templateToBackup, layoutsToBackup) => {
    setOriginalLayout(layoutsToBackup?.lg ? JSON.parse(JSON.stringify(layoutsToBackup.lg)) : []);
    setOriginalTemplate(templateToBackup ? JSON.parse(JSON.stringify(templateToBackup)) : null);
  }, []);

  /**
   * 백업 없이 편집 모드를 활성화합니다.
   */
  const enableEditModeWithoutBackup = useCallback(() => {
    if (isSavingLayout || editMode) return;

    // 편집 모드 진입 시 저장된 layout_mode 로드
    const currentDashboardTemplate = dashboardTemplateRef.current;
    if (currentDashboardTemplate && currentDashboardTemplate.dashboard_options) {
      const savedLayoutMode = currentDashboardTemplate.dashboard_options.layout_mode;
      if (savedLayoutMode && ['vertical', 'horizontal', 'none'].includes(savedLayoutMode)) {
        setCompactType(savedLayoutMode);
      }
    }
    
    setEditMode(true);
    document.body.classList.add('edit-mode-active');
  }, [editMode, isSavingLayout]);

  /**
   * 히스토리를 보존하면서 편집 모드를 활성화합니다.
   * @param {boolean} forcePreserveBackup - 백업 보존 강제 여부
   */
  const enableEditModeWithHistoryPreservation = useCallback((forcePreserveBackup = false) => {
    if (isSavingLayout || editMode) {
      return;
    }

    const shouldSkipBackup = forcePreserveBackup || originalTemplate;
    
    if (!shouldSkipBackup) {
      const currentDashboardTemplate = dashboardTemplateRef.current;
      
      setOriginalLayout(layouts.lg ? JSON.parse(JSON.stringify(layouts.lg)) : []);
      setOriginalTemplate(currentDashboardTemplate ? JSON.parse(JSON.stringify(currentDashboardTemplate)) : null);
      
    } else {
    }
    
    // 편집 모드 진입 시 저장된 layout_mode 로드
    const currentDashboardTemplate = dashboardTemplateRef.current;
    if (currentDashboardTemplate && currentDashboardTemplate.dashboard_options) {
      const savedLayoutMode = currentDashboardTemplate.dashboard_options.layout_mode;
      if (savedLayoutMode && ['vertical', 'horizontal', 'none'].includes(savedLayoutMode)) {
        setCompactType(savedLayoutMode);
      }
    }
    
    setEditMode(true);
    document.body.classList.add('edit-mode-active');
    
  }, [editMode, isSavingLayout, layouts, originalTemplate]);

  /**
   * 압축 타입을 토글합니다.
   */
  const toggleCompactType = useCallback(() => {
    setCompactType(prevType => {
      // 변경 전 상태를 undo 히스토리에 저장
      savePreActionState('layout_mode_change', 'layout_mode', `레이아웃 모드 변경: ${prevType} → `);
      
      let newType;
      if (prevType === 'vertical') newType = 'horizontal';
      else if (prevType === 'horizontal') newType = 'none';
      else newType = 'vertical';
      
      // 템플릿의 layout_mode도 함께 업데이트 (동기화 useEffect 방지)
      if (dashboardTemplate) {
        const updatedTemplate = {
          ...dashboardTemplate,
          dashboard_options: {
            ...dashboardTemplate.dashboard_options,
            layout_mode: newType
          }
        };
        setDashboardTemplateAndUpdateData(updatedTemplate);
      }
      
      // 변경사항이 있음을 표시
      setHasLayoutChanged(true);
      
      return newType;
    });
  }, [savePreActionState, setHasLayoutChanged, dashboardTemplate, setDashboardTemplateAndUpdateData]);
  
  /**
   * 컴포넌트를 레이아웃에 추가합니다.
   * @param {Object} newComponent - 새 컴포넌트
   * @param {boolean} skipHistorySaving - 히스토리 저장 건너뛰기 여부
   * @param {boolean} forceBottomPlacement - 강제 하단 배치 (챗봇 컴포넌트용)
   * @param {Object} templateData - 대시보드 템플릿 데이터 (옵션, 챗봇용)
   * @returns {Object} 새 레이아웃 아이템
   */
  const addComponentToLayout = useCallback((newComponent, skipHistorySaving = false, forceBottomPlacement = false, templateData = null) => {
    const { id, position, w = 6, h = 4 } = newComponent;
    
    // 강제 하단 배치인 경우(챗봇 컴포넌트) 자동 컴팩팅 비활성화
    if (forceBottomPlacement) {
      setIsChatbotAddingComponent(true);
    }
    
    let currentLayout = layouts.lg || [];
    
    // 템플릿 데이터 선택: 전달받은 templateData 우선, 없으면 기존 dashboardTemplate 사용
    const effectiveTemplate = templateData || dashboardTemplate;

    // 현재 컬럼 수에 비례하여 기본 크기(12x12 기준)를 스케일링
    const columns = (effectiveTemplate && effectiveTemplate.layout && typeof effectiveTemplate.layout.columns === 'number')
      ? effectiveTemplate.layout.columns
      : 12;
    const scale = columns / 12;
    const baseW = (position && typeof position.w === 'number') ? position.w : (typeof w === 'number' ? w : 6);
    const baseH = (position && typeof position.h === 'number') ? position.h : (typeof h === 'number' ? h : 4);
    let scaledW = Math.round(baseW * scale);
    let scaledH = Math.round(baseH * scale);
    // 최소/최대 보정: 너무 작지 않게, 컬럼 초과 금지
    const minAllowedW = Math.min(2, columns);
    if (Number.isNaN(scaledW)) scaledW = baseW;
    if (Number.isNaN(scaledH)) scaledH = baseH;
    scaledW = Math.min(columns, Math.max(minAllowedW, scaledW));
    scaledH = Math.max(2, scaledH);
    
    // 현재 레이아웃이 비어있는 경우, 대시보드 템플릿 컴포넌트에서 레이아웃 정보 추출
    if (currentLayout.length === 0 && effectiveTemplate?.components) {
      currentLayout = effectiveTemplate.components
        .filter(comp => comp && comp.id && comp.id !== id) // 추가하려는 컴포넌트는 제외
        .map(comp => ({
          i: comp.id,
          x: comp.position?.x || 0,
          y: comp.position?.y || 0,
          w: comp.position?.w || 6,
          h: comp.position?.h || 4
        }));
    } else if (currentLayout.length === 0) {
    }
    
    let finalPosition;

    // 강제 하단 배치가 활성화되었거나, position이 기본값(0, 0)인 경우 하단에 배치
    if (forceBottomPlacement || 
        !position || 
        (position.x === 0 && position.y === 0)) {
        finalPosition = findBestPositionForNewChart(currentLayout, scaledW, scaledH);
    } else if (position && typeof position.x === 'number' && typeof position.y === 'number') {
        finalPosition = { x: position.x, y: position.y, w: (typeof position.w === 'number' ? position.w : scaledW), h: (typeof position.h === 'number' ? position.h : scaledH) };
    } else {
        finalPosition = findBestPositionForNewChart(currentLayout, scaledW, scaledH);
    }
    
    const newLayoutItem = {
      i: id,
      x: finalPosition.x,
      y: finalPosition.y,
      w: finalPosition.w,
      h: finalPosition.h,
      minW: 1,
      minH: 1,
    };
    
    if (!skipHistorySaving) {
      executeWithHistory('add_component', id, `차트 추가: ${id}`, () => {
        customSetLayouts(prev => {
          // 모든 브레이크포인트에 동일한 레이아웃 아이템 추가
          const updatedLayouts = {};
          ['lg', 'md', 'sm', 'xs', 'xxs'].forEach(bp => {
            updatedLayouts[bp] = [...(prev[bp] || []), newLayoutItem];
          });
          return updatedLayouts;
        });
      });
    } else {
      customSetLayouts(prev => {
        // 모든 브레이크포인트에 동일한 레이아웃 아이템 추가
        const updatedLayouts = {};
        ['lg', 'md', 'sm', 'xs', 'xxs'].forEach(bp => {
          updatedLayouts[bp] = [...(prev[bp] || []), newLayoutItem];
        });
        return updatedLayouts;
      });
    }
    
    // 강제 하단 배치인 경우(챗봇 컴포넌트) 잠시 후 자동 컴팩팅 재활성화
    if (forceBottomPlacement) {
      setTimeout(() => {
        setIsChatbotAddingComponent(false);
      }, 3000); // 3초 후 재활성화 (더 오랜 시간 대기)
    }
    
    return newLayoutItem;
  }, [layouts, customSetLayouts, executeWithHistory]);

  // 컴포넌트 언마운트 시 RAF 정리
  useEffect(() => {
    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, []);

  return {
    editMode,
    setEditMode,
    layouts,
    setLayouts, // 새로 추가
    hasLayoutChanged,
    setHasLayoutChanged,
    isSavingLayout,
    setIsSavingLayout,
    compactType,
    setCompactType,
    isCancelling,
    setIsCancelling,
    isDragging, // 새로 추가
    setIsDragging, // 새로 추가
    draggedComponent, // 새로 추가
    setDraggedComponent, // 새로 추가
    toggleEditMode,
    saveLayout,
    cancelChanges,
    saveAndExit,
    exitWithoutSaving,
    toggleCompactType,
    syncLayoutAndTemplate,
    customSetLayouts,
    addComponentToLayout,
    removeComponentFromLayout,
    handleLayoutChange,
    handleBreakpointChange,
    handleDragStart,
    handleDrag,
    handleDragStop,
    handleResizeStart,
    handleResize,
    handleResizeStop,
    backupCurrentTemplate,
    backupSpecificTemplate,
    enableEditModeWithoutBackup,
    enableEditModeWithHistoryPreservation,
    canUndo,
    canRedo,
    undo,
    redo,
    clearHistory,
    addToHistory, // 새로 추가
    undoStack,
    redoStack,
    savePreActionState,
    isUndoRedoOperation,
    getEffectiveCompactType,
    isChatbotAddingComponent,
    setIsChatbotAddingComponent,
    createHistoryAction,
    getCurrentDashboardState,
    executeWithHistory,
    moveComponentByKeyboard,
    moveComponentsByKeyboard,
  };
}; 